import { fetchCmsRest } from '@/utils/request'
import {
  createViewInputQuery,
  getContentTypeIdentifier,
  mapAnnualPlan,
  mapCmsContent,
  mapCmsCredits,
  mapCmsExpandElement,
  mapCmsExternalLinks,
  mapCmsTopic,
  mapResourceGuide,
  mapSubject,
  mapSubjectGuide,
  mapTeacherGuide,
  mapTopicGuide,
} from '@/utils'

import type { ExpandElement } from '@/types/ExpandElement'
import type {
  IbexaBaseItemFields, IbexaField, Item, ViewInputRequest
} from '@/types/Ibexa'
import { IbexaContentTypeIdentifier } from '@/enums/IbexaContentTypeIdentifier'
import type { AnnualPlan, Subject } from '@/types/Subject'
import type { TeacherGuide } from '@/types/TeacherGuide'
import type { Topic } from '@/types/Topic'
import type { VgsContentBlock } from '@/types/VgsContentBlock'
import type { ExternalLink } from '@/types/ExternalLink'
import type { AnchorNavigationPage } from '@/types/AnchorNavigationPage'
import type { ISearchQuery } from '@/composables/useCmsContentSearch'
import { useCmsContentSearch } from '@/composables/useCmsContentSearch'
import type { Credits } from '@/types/Credits'
import type { SubjectGuide } from '@/types/SubjectGuide'

export const useContentApi = () => {
  const { search } = useCmsContentSearch()

  const postView = async <T>(body: ViewInputRequest, mappingFunction?: (item: Item) => T, fields: string[] = []): Promise<T[]> => {
    const path = `/views${fields.length > 0 ? `?fields=${fields.join(',')}` : ''}`
    const response = await fetchCmsRest(path, { method: 'POST', body: JSON.stringify(body) })
    const actualResponse = response.View.Result.searchHits.searchHit
    return mappingFunction ? actualResponse.map(mappingFunction) : actualResponse
  }

  const getContentFields = async (contentId: number | string): Promise<IbexaField[]> => {
    const response = await fetchCmsRest('/content/objects/' + contentId + '/currentversion')
    return response.Version.Fields.field
  }

  const createSingleElementQuery = (elementContentId: number) => {
    return createViewInputQuery({
      identifier: 'current_element',
      contentId: elementContentId,
      limit: '1'
    })
  }

  const getCurrentElementByLocationId = async<T>(locationId: string): Promise<T> => {
    const query = createViewInputQuery({
      identifier: 'current_element_by_location_id',
      locationId: locationId,
      limit: '1'
    })

    const response = await postView<VgsContentBlock | IbexaBaseItemFields>(query, mapCmsContent)

    return response[0] as T
  }

  async function getAllSubjects(): Promise<Subject[]> {
    const query: ISearchQuery = {
      fields: 'title,short_title,programs,levels',
      ['contentTypes[]']: IbexaContentTypeIdentifier.vgsProduct
    }

    const searchResult = await search(query)

    return searchResult.map((item: Item) => mapSubject(item))
  }

  const getCurrentSubject = async (subjectContentId: number): Promise<Subject> => {
    const query = {
      fields: 'title,short_title,programs,levels,description,color_pair,image,authors,annual_plan',
      ['contentId[]']: subjectContentId.toString(),
      ['contentTypes[]']: IbexaContentTypeIdentifier.vgsProduct,
    }

    const searchResult = await search(query)

    return mapSubject(searchResult[0])
  }

  async function getAnnualPlan(contentId: string): Promise<AnnualPlan> {
    const query: ISearchQuery = {
      fields: 'name,short_name,file',
      ['contentTypes[]']: IbexaContentTypeIdentifier.vgsFile,
      ['contentId[]']: contentId,
    }

    const searchResult = await search(query)

    return mapAnnualPlan(searchResult[0])
  }

  const getCurrentElement = async <T>(contentId: number): Promise<T> => {
    const query = createSingleElementQuery(contentId)

    const response = await postView<VgsContentBlock | IbexaBaseItemFields>(query, mapCmsContent)
    return response[0] as T
  }

  type TopicChildren = {
    topics: Topic[]
    credits?: Credits
  }

  const getTopicsAndCredits = async (parentContentId: number): Promise<TopicChildren> => {
    const parent = await getCurrentElement(parentContentId) as IbexaBaseItemFields

    const query: ISearchQuery = {
      fields: 'title,title_with_html,subtitle,intro,is_coming_soon,authors,editors,collaborators,bibliography',
      ['contentTypes[]']: [IbexaContentTypeIdentifier.vgsCredits, IbexaContentTypeIdentifier.vgsTopic],
      ['parentLocationId[]']: parent?.locationId,
    }

    const searchResult = await search(query)

    const result = searchResult.reduce((acc: TopicChildren, item: Item) => {
      const identifierName = getContentTypeIdentifier(item)

      if (identifierName === IbexaContentTypeIdentifier.vgsTopic) {
        acc.topics.push(mapCmsTopic(item))
      } else {
        acc.credits = mapCmsCredits(item)
      }
      return acc
    }, { topics: [], credits: undefined })

    return result
  }

  const getResourceGuideChild = async (parentContentId: number): Promise<AnchorNavigationPage | null> => {
    const parent = await getCurrentElement(parentContentId) as IbexaBaseItemFields
    const query = createViewInputQuery({
      identifier: 'resource_guide',
      contentTypeId: IbexaContentTypeIdentifier.vgsResourceGuide,
      parentLocationId: parent.locationId,
      limit: '1'
    })

    const response = await postView<AnchorNavigationPage>(query, mapResourceGuide)
    return response.length ? response[0] : null
  }

  const getTopicGuideChild = async (parentContentId: number): Promise<AnchorNavigationPage> => {
    const parent = await getCurrentElement(parentContentId) as IbexaBaseItemFields
    const query = createViewInputQuery({
      identifier: 'topic_guide',
      contentTypeId: IbexaContentTypeIdentifier.vgsTopicGuide,
      parentLocationId: parent.locationId,
      limit: '1'
    })

    const response = await postView<AnchorNavigationPage>(query, mapTopicGuide)
    return response[0] ?? null
  }

  const getSubjectGuideChild = async (parentContentId: number): Promise<SubjectGuide> => {
    const parent = await getCurrentElement(parentContentId) as IbexaBaseItemFields
    const query = createViewInputQuery({
      identifier: 'subject_guide',
      contentTypeId: IbexaContentTypeIdentifier.vgsSubjectGuide,
      parentLocationId: parent.locationId,
      limit: '1'
    })

    const response = await postView<SubjectGuide>(query, mapSubjectGuide)
    return response[0] ?? null
  }

  const getTeacherGuideChildren = async (parentContentId: number): Promise<TeacherGuide> => {
    const parent = await getCurrentElement(parentContentId) as IbexaBaseItemFields
    const query = createViewInputQuery({
      identifier: 'teacher_guide',
      contentTypeId: IbexaContentTypeIdentifier.vgsTeacherGuide,
      parentLocationId: parent.locationId,
      limit: '1'
    })
    const response = await postView<TeacherGuide>(query, mapTeacherGuide)
    return response[0]
  }

  const getExpandElementChildren = async (parentLocationId: string): Promise<ExpandElement[]> => {
    const query = createViewInputQuery({
      identifier: 'expand_element_children',
      contentTypeId: IbexaContentTypeIdentifier.vgsExpandElement,
      parentLocationId: parentLocationId,
      isSorted: true
    })

    return await postView<ExpandElement>(query, mapCmsExpandElement)
  }

  const getAllExternalLinks = async (parentLocationId: string): Promise<ExternalLink[]> => {
    const query = createViewInputQuery({
      identifier: 'external_links',
      contentTypeId: IbexaContentTypeIdentifier.vgsExternalLink,
      parentLocationId: parentLocationId,
      isSorted: true,
    })
    return await postView<ExternalLink>(query, mapCmsExternalLinks)
  }

  const getCurrentResourceGuide = async (resourceGuideContentId: number): Promise<AnchorNavigationPage> => {
    const response = await postView<AnchorNavigationPage>(createSingleElementQuery(resourceGuideContentId), mapResourceGuide)
    return response[0]
  }

  const getCurrentTopicGuide = async (topicGuideContentId: number): Promise<AnchorNavigationPage> => {
    const response = await postView<AnchorNavigationPage>(createSingleElementQuery(topicGuideContentId), mapTopicGuide)
    return response[0]
  }

  const getCurrentSubjectGuide = async (subjectGuideContentId: number): Promise<SubjectGuide> => {
    const response = await postView<SubjectGuide>(createSingleElementQuery(subjectGuideContentId), mapSubjectGuide)
    return response[0]
  }

  const getCurrentTopic = async (topicContentId: number): Promise<Topic> => {
    const parent = await getCurrentElement<Topic>(topicContentId)

    const query: ISearchQuery = {
      ['contentTypes[]']: IbexaContentTypeIdentifier.vgsTopic,
      ['locationId[]']: parent.locationId,
      limit: '1',
      offset: '0'
    }

    const searchResult = await search(query)
    const response = searchResult.map((item: Item) => {
      return mapCmsTopic(item)
    })

    return response[0]
  }

  const getTopicSubtree = async (pathString: string): Promise<Item[]> => {
    return await postView<Item>(createViewInputQuery({
      identifier: 'topic_subtree',
      contentTypeId: [IbexaContentTypeIdentifier.vgsSubjectResource, IbexaContentTypeIdentifier.vgsSubjectContent],
      pathString: pathString,
      limit: '999',
      offset: '0',
      isSorted: true,
    }), undefined, ['title', 'title_with_html', 'thumbnail', 'description', 'category'])
  }

  const getAllChildrenElements = async (parentContentId: number): Promise<VgsContentBlock[]> => {
    const parent = await getCurrentElement(parentContentId) as IbexaBaseItemFields
    const query: ISearchQuery = { ['parentLocationId[]']: parent.locationId }
    const searchResult = await search(query)
    const response = searchResult.map((item: Item) => {
      return mapCmsContent(item)
    })
    return response.filter(i => i !== undefined) as Array<VgsContentBlock>
  }

  const getSubjectResourceChildren = async (subjectResourceContentId: number): Promise<VgsContentBlock[]> => {
    const parent = await getCurrentElement(subjectResourceContentId) as IbexaBaseItemFields
    const query = createViewInputQuery({
      identifier: 'subject_resource_children',
      parentLocationId: parent?.locationId ?? '',
      contentTypeId: [
        IbexaContentTypeIdentifier.vgsStepTask,
        IbexaContentTypeIdentifier.vgsFlytTask,
        IbexaContentTypeIdentifier.vgsTextImageElement, // Can be deleted after VGS-473
        IbexaContentTypeIdentifier.vgsFolder,
        IbexaContentTypeIdentifier.vgsTaskFrontpage,
        IbexaContentTypeIdentifier.vgsResourceGuide,
      ],
      isSorted: true
    })

    return await postView<VgsContentBlock>(query, mapCmsContent)
  }

  return {
    getAllChildrenElements,
    getAllExternalLinks,
    getAllSubjects,
    getContentFields,
    getCurrentElement,
    getCurrentElementByLocationId,
    getCurrentResourceGuide,
    getCurrentSubject,
    getCurrentSubjectGuide,
    getCurrentTopic,
    getCurrentTopicGuide,
    getExpandElementChildren,
    getResourceGuideChild,
    getSubjectGuideChild,
    getSubjectResourceChildren,
    getTeacherGuideChildren,
    getTopicGuideChild,
    getTopicsAndCredits,
    getTopicSubtree,
    getAnnualPlan
  }
}
